import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useEnvironmentStore } from '@/lib/environments/store';
import { SecretsManager } from '@/components/environments/secrets-manager';
import { ArrowLeft, AlertTriangle, Server, Rocket } from 'lucide-react';

export const Route = createFileRoute('/_dashboard/environments/$id')({
  component: EnvironmentDetailPage,
});

function EnvironmentDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const {
    selectedEnvironment,
    environmentsLoading,
    environmentsError,
    loadEnvironment,
  } = useEnvironmentStore();

  useEffect(() => {
    loadEnvironment(id);
  }, [id, loadEnvironment]);

  if (environmentsLoading && !selectedEnvironment) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  if (environmentsError) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="size-4" />
        <AlertDescription>{environmentsError}</AlertDescription>
      </Alert>
    );
  }

  if (!selectedEnvironment) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Environment not found</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate({ to: '/environments' })}
        >
          Back to Environments
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate({ to: '/environments' })}
        >
          <ArrowLeft className="size-4" />
        </Button>
        <div className="flex items-center gap-3">
          <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Server className="size-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">{selectedEnvironment.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="secondary">{selectedEnvironment.type}</Badge>
              <Badge variant={selectedEnvironment.status === 'active' ? 'default' : 'secondary'}>
                {selectedEnvironment.status}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="secrets">Secrets</TabsTrigger>
          <TabsTrigger value="deployments">Deployments</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Environment Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Name</span>
                  <span className="font-medium">{selectedEnvironment.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Type</span>
                  <span className="font-medium capitalize">{selectedEnvironment.type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <Badge variant={selectedEnvironment.status === 'active' ? 'default' : 'secondary'}>
                    {selectedEnvironment.status}
                  </Badge>
                </div>
                {selectedEnvironment.description && (
                  <div>
                    <span className="text-muted-foreground block mb-1">Description</span>
                    <p className="text-sm">{selectedEnvironment.description}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Variables</CardTitle>
              </CardHeader>
              <CardContent>
                {selectedEnvironment.variables && Object.keys(selectedEnvironment.variables).length > 0 ? (
                  <pre className="text-sm bg-muted p-3 rounded overflow-auto max-h-48">
                    {JSON.stringify(selectedEnvironment.variables, null, 2)}
                  </pre>
                ) : (
                  <p className="text-muted-foreground text-sm">No variables set</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="secrets" className="mt-6">
          <SecretsManager environmentId={selectedEnvironment.id} />
        </TabsContent>

        <TabsContent value="deployments" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Rocket className="size-5" />
                Deployments
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground text-sm">
                View and manage agent deployments for this environment from the Agents page.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
